import { useMemo, useState } from "react";
import useSeo from "@/hooks/useSeo";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/feature/ArticleCard";
import Container from "@/components/base/Container";
import { articles } from "@/mocks/articles";
import { articleCategories } from "@/mocks/site";

export default function ArticleListPage() {
  const [category, setCategory] = useState("all");
  const [keyword, setKeyword] = useState("");

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return articles.filter((item) => {
      const matchCategory = category === "all" || item.category === category;
      const matchKeyword =
        !kw ||
        item.title.toLowerCase().includes(kw) ||
        item.excerpt.toLowerCase().includes(kw) ||
        item.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCategory && matchKeyword;
    });
  }, [category, keyword]);

  useSeo({
    title: "深度解读 | 网红明星跨界对比的方法与观察-咖位观察局",
    description:
      "咖位观察局的深度解读栏目，分享咖位评估方法论、同框解析、流量观察与商业价值分析，帮你把一场娱乐热闹读成可复核的门道，理解网红明星差距与热度变迁。",
    keywords: "网红明星解读,咖位方法论,同框解析,流量观察,跨界对比,热度走势",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "CollectionPage",
          name: "深度解读",
          description: "网红明星跨界对比的方法与观察文章集合。",
          inLanguage: "zh-CN",
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "/" },
            { "@type": "ListItem", position: 2, name: "深度解读" },
          ],
        },
      ],
    },
  });

  return (
    <>
      <SiteHeader />
      <main className="pt-16 md:pt-20">
        <Breadcrumbs items={[{ label: "首页", path: "/" }, { label: "深度解读" }]} />

        <section className="border-b border-background-200/80 bg-background-100 py-12 md:py-16">
          <Container>
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] text-primary-600">
                  <span className="h-px w-6 bg-primary-500" />
                  ARTICLES
                </span>
                <h1 className="mt-4 font-heading text-3xl font-bold leading-snug text-foreground-950 md:text-4xl">
                  深度解读
                </h1>
                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-[15px]">
                  方法、细节与趋势，是我们反复回到的三个落点。这里有评估框架，也有同框拆解，帮你在讨论里多一个可以站得住的视角。
                </p>
              </div>

              <div className="lg:col-span-5">
                <label htmlFor="article-search" className="mb-2 block text-xs font-medium text-foreground-600">
                  搜索解读
                </label>
                <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5">
                  <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
                    <i className="ri-search-line text-lg" />
                  </span>
                  <input
                    id="article-search"
                    type="search"
                    value={keyword}
                    onChange={(event) => setKeyword(event.target.value)}
                    placeholder="输入关键词，例如 咖位 / 同框 / 代言"
                    className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                  />
                  {keyword ? (
                    <button
                      type="button"
                      aria-label="清除搜索"
                      onClick={() => setKeyword("")}
                      className="flex h-5 w-5 items-center justify-center text-foreground-400 hover:text-foreground-700"
                    >
                      <i className="ri-close-line text-base" />
                    </button>
                  ) : null}
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {articleCategories.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setCategory(item.key)}
                      className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                        category === item.key
                          ? "bg-primary-500 text-background-50"
                          : "border border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </Container>
        </section>

        <section className="bg-background-50 py-12 md:py-16">
          <Container>
            <p className="mb-6 text-sm text-foreground-500">
              共找到 <strong className="text-foreground-900">{filtered.length}</strong> 篇文章
            </p>

            {filtered.length > 0 ? (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {filtered.map((item) => (
                  <ArticleCard
                    key={item.slug}
                    slug={item.slug}
                    title={item.title}
                    category={item.category}
                    excerpt={item.excerpt}
                    coverImage={item.coverImage}
                    date={item.date}
                    readTime={item.readTime}
                    views={item.views}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-background-200 bg-background-100 px-6 py-16 text-center">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-search-eye-line text-2xl" />
                </span>
                <p className="mt-4 text-sm text-foreground-600">没有匹配的文章，换个关键词或分类试试。</p>
                <button
                  type="button"
                  onClick={() => {
                    setKeyword("");
                    setCategory("all");
                  }}
                  className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  重置筛选
                </button>
              </div>
            )}
          </Container>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}